import { useState } from "react";
import "./DownloadEvents.css";
import Modal from "ui-kit/src/Modal";
import { Badge } from "ui-kit";
import { Field, Form } from "react-final-form";
import { useVideoPlayer } from "../../../Contexts/contexts";
import { EventFilterValue, FilterCategory, type GameEventDataExportRequest, GPEventResponse } from "../../../types";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { ValidationErrors } from "final-form";
import { useGameStore } from "../../../store";
import { useFullscreenModalContainer } from "../../../useFullscreenModalContainer";
import { Download, Loader2 } from "lucide-react";

interface Option {
	key: string;
	value: string;
	label: string;
}

interface FormValues {
	format: "xml" | "json" | "csv" | "xlsx";
	teams: boolean[];
	periods: boolean[];
	type: "all" | "filtered";
}

interface DownloadEventsProps {
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void | Promise<void>;
	allowedPlayers: number[] | null;
}

export function DownloadEvents({ handleGameEventDataExport, allowedPlayers }: DownloadEventsProps) {
	const [showModal, setShowModal] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const { playerRef } = useVideoPlayer();
	const fullscreenModalContainer = useFullscreenModalContainer();

	const { events, eventFilter } = useGameStore();

	const downloadAll = allowedPlayers === null;

	const periods = eventFilter.getFilterViewsByCategories([FilterCategory.Period]).map(
		(fw) =>
			({
				key: fw.key,
				value: fw.value,
				label: _(fw.label as keyof Keys),
			}) as Option,
	);

	const teams = eventFilter.getFilterViewsByCategories([FilterCategory.Team]).map(
		(fw) =>
			({
				key: fw.key,
				value: fw.value,
				label: _(fw.label as keyof Keys),
			}) as Option,
	);

	const initialFormValues = {
		format: "xml",
		type: "filtered",
		periods: periods.map(() => true),
		teams: teams.map(() => true),
	} as FormValues;

	const onSubmit = async (values: FormValues) => {
		let eventsToDownload: GPEventResponse[] = [];
		if (values.type === "filtered") {
			eventsToDownload = events;
		} else {
			let filterValues: EventFilterValue[] = [];

			values.periods.forEach((val, index) => {
				if (val) {
					const option = periods[index] as Option;
					filterValues = eventFilter.mutateFilterValues(filterValues, option.key, option.value);
				}
			});

			values.teams.forEach((val, index) => {
				if (val) {
					const option = teams[index] as Option;
					filterValues = eventFilter.mutateFilterValues(filterValues, option.key, option.value);
				}
			});

			eventsToDownload = eventFilter.filter(filterValues);
		}

		setIsExporting(true);
		try {
			await handleGameEventDataExport({
				extension: values.format,
				events: eventsToDownload.map((e) => e.id),
			} as GameEventDataExportRequest);
			setShowModal(false);
		} finally {
			setIsExporting(false);
		}
	};

	const onValidate = (values: FormValues): ValidationErrors | Promise<ValidationErrors> => {
		const errors: any = {};

		if (values.type === "all" && (values.periods.filter((p) => p).length === 0 || values.teams.filter((t) => t).length === 0)) {
			errors.type = _("label.error.invalidForm");
		}

		return errors;
	};

	return (
		<>
		<button
			type="button"
			className="download-button"
			disabled={isExporting}
			aria-busy={isExporting}
			onClick={() => {
				if (playerRef?.current) {
					playerRef.current.pause();
				}
				setShowModal(true);
			}}
		>
			{isExporting ? (
				<Loader2 size={24} strokeWidth={1.5} className="event-export__triggerSpinner" aria-hidden />
			) : (
				<Download size={24} strokeWidth={1.5} />
			)}
		</button>
			<Modal
				title={_("gamePage.exportEvents.title")}
				show={showModal}
				size="xs"
				onClose={isExporting ? undefined : () => setShowModal(false)}
				UNSTABLE_portalContainer={fullscreenModalContainer}
			>
				<Form
					initialValues={initialFormValues}
					onSubmit={onSubmit}
					validate={onValidate}
					render={({ handleSubmit, valid, values }) => (
						<form onSubmit={handleSubmit} className={`event-export${isExporting ? " event-export--loading" : ""}`}>
							{isExporting && (
								<div className="event-export__loadingOverlay" aria-live="polite" aria-busy="true">
									<Loader2 size={32} strokeWidth={1.5} className="event-export__loadingSpinner" aria-hidden />
									<span>{_("datatables.loading_records")}</span>
								</div>
							)}
							<div className="event-export__title">{_("stats.export.title")}</div>
							<Field name="format">
								{({ input, meta }) => (
									<div className="event-export__formats">
										{["xml", "json", "csv", "xlsx"].map((format: string) => (
											<span key={format}>
												<input id={`export-format-${format}`} type="radio" {...input} value={format} />
												<label htmlFor={`export-format-${format}`}>
													<BadgeAdapter label={`.${format}`} active={input.value === format} block={true} />
												</label>
											</span>
										))}
									</div>
								)}
							</Field>
							<div className="event-export__title">{_("stats.export.events")}</div>
							<Field name="type">
								{({ input, meta }) => (
									<div className="event-export__types">
										<span key="filtered">
											<input id="export-type-filtered" type="radio" {...input} value="filtered" />
											<label htmlFor="export-type-filtered">
												<BadgeAdapter
													label={_("label.gamePage.filteredEvents")}
													active={input.value === "filtered"}
													block={true}
												/>
											</label>
										</span>
										<span key="all">
											<input id="export-type-all" type="radio" {...input} value="all" disabled={!downloadAll} />
											<label htmlFor="export-type-all">
												<BadgeAdapter
													label={_("label.download_all")}
													active={input.value === "all"}
													block={true}
													disabled={!downloadAll}
												/>
											</label>
										</span>
									</div>
								)}
							</Field>
							{values.type === "all" && (
								<>
									<div className="event-export__title">{_("stats.export.periods")}</div>
									<div className="event-export__blocks">
										{periods.map((period: Option, index) => (
											<Field name={`periods[${index}]`} key={`period-${period.key}`}>
												{({ input, meta }) => (
													<div key={period.key}>
														<input
															id={`export-period-${period.key}`}
															type="checkbox"
															{...input}
															checked={!!input.value}
														/>
														<label htmlFor={`export-period-${period.key}`}>
															<BadgeAdapter label={period.label} active={!!input.value} icon={!!input.value} />
														</label>
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</div>
												)}
											</Field>
										))}
									</div>
									<div className="event-export__title">{_("stats.export.teams")}</div>
									<div className="event-export__blocks">
										{teams.map((team: Option, index) => (
											<Field name={`teams.${index}`} key={`team-${team.key}`}>
												{({ input, meta }) => (
													<div key={team.key}>
														<input id={`export-team-${team.key}`} type="checkbox" {...input} checked={!!input.value} />
														<label htmlFor={`export-team-${team.key}`}>
															<BadgeAdapter label={team.label} active={!!input.value} icon={!!input.value} />
														</label>
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</div>
												)}
											</Field>
										))}
									</div>
								</>
							)}
							<button type="submit" disabled={!valid || isExporting}>
								<Badge
									label={isExporting ? "datatables.loading_records" : "label.btn.export"}
									block={true}
									variant="primary"
									onClick={() => {}}
									disabled={!valid || isExporting}
								/>
							</button>
						</form>
					)}
				/>
			</Modal>
		</>
	);
}

interface BadgeAdapterProps {
	label: string;
	active: boolean;
	icon?: boolean;
	block?: boolean;
	disabled?: boolean;
}

const BadgeAdapter = ({ label, active, disabled, icon = false, block = true }: BadgeAdapterProps) => {
	return (
		<Badge
			label={label}
			variant={active ? "secondary" : "default"}
			disabled={disabled}
			outline={!active}
			block={block}
			icon={icon ? "check" : undefined}
			onClick={() => {}}
		/>
	);
};
